// Classifier for Medicine Tag List API responses.
//
// The endpoint returns business outcomes as HTTP 200 with the meaningful code
// in `body.statusCode` — callers MUST branch on that, not the HTTP status.
// This helper normalizes the response into a single shape for all callers.
//
// Per spec: statusCode 200 = success (payload in message as JSON string),
// 451 = business validation failure (e.g., duplicate tag name, invalid reference),
// 400 or other = system error.

export const MedicineTagListOutcome = {
  SUCCESS: "success",           // 200 — saved/fetched successfully
  BUSINESS_REJECTION: "business_rejection", // 451 — not saved; reason in `message`
  ERROR: "error",               // 400 / unexpected — generic failure
};

// Normalizes a parsed medicine-tag-list response body into:
//   { outcome, data, message, statusCode }
// - data:    parsed DTO from `message` on success (null if absent/unparseable)
// - message: human-readable reason for BUSINESS_REJECTION / ERROR (else null)
export function classifyMedicineTagListResponse(res) {
  const statusCode = res && res.statusCode;

  if (statusCode === 200) {
    let data = null;
    if (res.message) {
      try {
        data = JSON.parse(res.message);
      } catch (e) {
        // 200 success payload should always be a JSON string; if it is not,
        // treat it as a successful-but-empty response rather than a hard failure.
        data = null;
      }
    }

    return { outcome: MedicineTagListOutcome.SUCCESS, data, message: null, statusCode };
  }

  if (statusCode === 451) {
    return {
      outcome: MedicineTagListOutcome.BUSINESS_REJECTION,
      data: null,
      message: (res && res.message) || "Request was rejected. Please review and try again.",
      statusCode,
    };
  }

  // 400 / unexpected / missing body / non-2xx HTTP status
  return {
    outcome: MedicineTagListOutcome.ERROR,
    data: null,
    message: "Something went wrong. Please try again.",
    statusCode,
  };
}
